<!-- @format -->
<template>
    <div class="gst-cancellation-policies d-flex flex-column px-4 py-4 gst-senary-border">
        <h3 class="gst-cancellation-policies__title">{{ $t('title') }}</h3>
        <div
            v-for="(item, index) in cancellationPolicies"
            :key="index"
            class="gst-cancellation-policies__item"
            :class="{
                'gst-cancellation-policies__item--current': item.isCurrent,
                'gst-cancellation-policies__item--past': item.isPast && !item.isCurrent,
            }">
            {{ item.description }}
        </div>
        <hr
            class="gst-hr u-width-100 mt-2 mb-4"
            :class="{ 'd-none': !isCancelFeatureEnabled }" />
        <CancellationBooking v-if="isCancelFeatureEnabled" />
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import currencyFull from '@core/filters/currencyFull';
import dateConstants from '@core/utils/constants/date.js';
import { isBefore, isAfter } from '@tenant/app/utils/dateUtils.js';
import CancellationBooking from './CancellationBooking.vue';

export default {
    name: 'CancellationPolicies',
    i18nOptions: {
        namespaces: 'main',
        keyPrefix: 'modules.hotel.features.manageBooking._components.cancellationPolicies',
    },
    components: {
        CancellationBooking,
    },
    props: {
        policies: {
            type: Array,
            required: true,
        },
        currency: {
            type: String,
            default: 'USD',
        },
        bookingStatus: {
            type: String,
            default: '',
        },
    },
    computed: {
        ...mapGetters({
            isCancelFeatureEnabled: 'booking/isCancelEnabled',
        }),
        cancellationPolicies() {
            const filteredPolicies = this.policies.reduce((acc, policy, index, policies) => {
                const date = new Date(policy.fromAt);
                const today = new Date();
                const amount = policy.nonRefundable
                    ? this.$t('variants.bookingCancellationFullCharge')
                    : currencyFull(policy.amount, this.currency);
                const isCurrentPolicy =
                    isBefore(date, today) &&
                    (!policies[index + 1] || isAfter(new Date(policies[index + 1].fromAt), today));
                const isPastPolicy = isBefore(date, today);

                if (!index) {
                    acc.push({
                        description: this.$t('variants.bookingCancellationFree', {
                            date: this.$options.filters.date(date, dateConstants.FORMAT.UI_DATE),
                            time: this.$options.filters.date(date, dateConstants.FORMAT.TIME),
                        }),
                        freeCancellation: true,
                        isCurrent: isAfter(date, today),
                        isPast: isPastPolicy,
                    });
                }
                acc.push({
                    description: this.$t('variants.bookingCancellationCharge', {
                        date: this.$options.filters.date(date, dateConstants.FORMAT.UI_DATE),
                        time: this.$options.filters.date(date, dateConstants.FORMAT.TIME),
                        amount,
                    }),
                    from: policy.fromAt,
                    isCurrent: isCurrentPolicy,
                    isPast: isPastPolicy,
                });

                return acc;
            }, []);

            return filteredPolicies;
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@scssVariables';
@import '@scssMixins';

.gst-cancellation-policies {
    .gst-cancellation-policies__title {
        color: theme-color('quaternary');
        font-size: font-size('xxl');
        font-weight: font-weight('large');
    }

    .gst-cancellation-policies__item {
        margin: theme-spacing(2, 0);
        color: theme-color('quaternary');
        font-size: font-size('s');
        font-weight: font-weight('regular');
    }

    .gst-cancellation-policies__item--current {
        position: relative;
        padding-left: theme-spacing(2);
        font-weight: font-weight('large');
    }

    .gst-cancellation-policies__item--current::before {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 4px;
        background-color: theme-color('success');
        border-radius: 20%;
        content: '';
    }

    .gst-cancellation-policies__item--past {
        color: theme-color('tertiary');
    }

    @include mobile-only {
        .gst-cancellation-policies__title {
            font-size: font-size('l');
        }
    }
}
</style>
